import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { hasAdminAccess } from "@/lib/admin-access";
import { listEmailLogs } from "@/lib/admin-email.functions";

export const Route = createFileRoute("/admin/logs")({
  head: () => ({
    meta: [
      { title: "Email Activity · Admin · WeLivdIt" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminLogsPage,
});

type LogItem = {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  recipient_name: string | null;
  subject: string;
  status: string;
  created_at: string;
  category: "enrolled" | "lead";
  error_message: string | null;
};

function AdminLogsPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<LogItem[]>([]);
  const [stats, setStats] = useState({ total: 0, enrolled: 0, leads: 0, deliveryRate: 0 });
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const list$ = useServerFn(listEmailLogs);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      const ok = data.session ? await hasAdminAccess(data.session) : false;
      if (!ok) {
        navigate({ to: "/auth" });
        return;
      }
      setAuthChecked(true);
      try {
        const r = await list$();
        setItems(r.items as any);
        setStats(r.stats);
      } catch (e: any) {
        toast.error("Failed to load logs", { description: e?.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const filtered = useMemo(
    () => (statusFilter === "all" ? items : items.filter((i) => i.status === statusFilter)),
    [items, statusFilter],
  );

  if (!authChecked) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-5 py-10 space-y-6">
      <div>
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </Link>
        <div className="mt-4 flex items-center gap-2">
          <MailCheck className="h-5 w-5 text-primary" />
          <h1 className="font-display text-3xl font-semibold">Email Activity</h1>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard label="Total Emails Sent" value={stats.total} />
        <StatCard label="Enrolled Recipients" value={stats.enrolled} />
        <StatCard label="Unenrolled Leads" value={stats.leads} />
        <StatCard label="Delivery Rate" value={`${stats.deliveryRate}%`} />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-foreground/70">Filter:</span>
        {["all", "sent", "pending", "failed", "dlq", "suppressed", "unsubscribed"].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${
              statusFilter === s
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background border-border hover:bg-accent"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="p-10 grid place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-foreground/60">No email activity yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-foreground/60">
                <tr>
                  <th className="px-4 py-3">Recipient</th>
                  <th className="px-4 py-3">Audience</th>
                  <th className="px-4 py-3">Subject / Template</th>
                  <th className="px-4 py-3">Sent</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((it) => (
                  <tr key={it.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{it.recipient_name || "—"}</div>
                      <div className="text-xs text-foreground/60">{it.recipient_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <CategoryBadge category={it.category} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs font-medium">{it.subject}</div>
                      <div className="text-[11px] text-foreground/60">{it.template_name}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground/70">
                      {format(parseISO(it.created_at), "dd MMM · HH:mm")}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={it.status} />
                      {it.error_message && (
                        <div className="text-[10px] text-red-500 mt-0.5 max-w-[220px] truncate" title={it.error_message}>
                          {it.error_message}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="text-[11px] uppercase tracking-wider font-bold text-foreground/60">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function CategoryBadge({ category }: { category: "enrolled" | "lead" }) {
  if (category === "enrolled") {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2.5 py-0.5 text-[11px] font-semibold">
        Enrolled Student
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2.5 py-0.5 text-[11px] font-semibold">
      Unenrolled Lead
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    sent: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    pending: "bg-slate-500/10 text-slate-700 dark:text-slate-300",
    failed: "bg-red-500/10 text-red-700 dark:text-red-400",
    dlq: "bg-red-500/10 text-red-700 dark:text-red-400",
    bounced: "bg-red-500/10 text-red-700 dark:text-red-400",
    suppressed: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    unsubscribed: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    opened: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
  };
  const cls = map[status] || "bg-slate-500/10 text-slate-700 dark:text-slate-300";
  const label =
    status === "sent"
      ? "Delivered"
      : status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${cls}`}>
      {label}
    </span>
  );
}
